import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MdDashboard, MdPeople, MdFactory, MdInventory2,
  MdReceipt, MdBarChart, MdAdminPanelSettings,
  MdChevronLeft, MdChevronRight, MdLogout,
  MdApartment, MdGroup, MdManageAccounts,
  MdLocalPharmacy, MdWarehouse, MdLocalShipping, MdAttachMoney, MdTrendingUp,
  MdCalendarMonth, MdLocalHospital, MdAssessment, MdPayment,
  MdCreditCard, MdPriceChange, MdContactMail,
  MdBusiness, MdUndo, MdSettings, MdSecurity, MdManageHistory, MdShoppingCart,
  MdAutorenew, MdTableChart, MdEngineering, MdCoPresent, MdPrecisionManufacturing, MdEdit,
  MdAccountBalance, MdStorefront, MdBuild, MdLocationOn, MdShare, MdStar, MdLanguage, MdQrCode2, MdCategory, MdStraighten,
} from 'react-icons/md';
import { logout, selectUser, selectRole, selectModules, selectAuditLogEnabled, selectTenantName, selectTenantLogoUrl, selectPermissions, selectEnabledFeatures, selectFeatureLabel, selectNavigationLabels, selectAdminTenantCleanupEnabled, selectIsFeatureEnabled, selectSubscriptionActive } from '../../store/authSlice';
import { selectI18nEnabled, selectSupportedLanguages, selectCurrentLanguage, setCurrentLanguage } from '../../store/i18nSlice';
import { SUPPORTED_LANGUAGES } from '../../utils/i18nConfig';
import { selectSidebar, toggleSidebar, setSidebarOpen } from '../../store/uiSlice';
import { authApi } from '../../api/authApi';
import useTranslation from '../../hooks/useTranslation';

// =============================================================================
// NAV_ITEMS — single source of truth for every sidebar tab.
// Now uses translation keys instead of hardcoded labels.
// Translation key format: 'navigation.itemName' (see locales/*.json)
//
// Three independent filters are applied in sequence (all must pass):
//   1. roles[]          — coarse role gate (who could ever see this tab)
//   2. module / requiresAnyModule — tenant must have purchased this module
//   3. permission       — DB-driven key from role_default_permissions →
//                         tenant_role_permissions → user_permission_overrides
//                         Tenant Admin can add/revoke these per role in their
//                         workspace without any code deployment.
//
// Role name mapping (current code → matrix terminology):
//   MANAGER = cross-module manager (operations, billing, reports)
//   STAFF   = operational worker (module guard controls which domain tabs appear)
//   DOCTOR          = DOCTOR   (clinical specialist)
// =============================================================================
const NAV_ITEMS = [

  // ── Dashboard ─────────────────────────────────────────────────────────────
  { labelKey: 'navigation.dashboard', icon: MdDashboard, to: '/dashboard',
    roles: ['SUPER_ADMIN','ADMIN','DOCTOR','STAFF','MANAGER','READ_ONLY'] },

  // ── Super Admin workspace management (non-audit items) ────────────────────
  { labelKey: 'navigation.workspaces',          icon: MdApartment,    to: '/super-admin/tenants',         roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.allUsers',           icon: MdGroup,        to: '/super-admin/users',            roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.subscriptionReport', icon: MdCreditCard,   to: '/super-admin/subscriptions',    roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.planPricing',        icon: MdPriceChange,  to: '/super-admin/plan-pricing',     roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.contactMessages',    icon: MdContactMail,  to: '/super-admin/contact-messages', roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.mfaSecurity',        icon: MdSecurity,     to: '/super-admin/mfa-setup',        roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.renewalRequests',    icon: MdAutorenew,    to: '/super-admin/renewals',         roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.roleMatrix',         icon: MdTableChart,   to: '/super-admin/role-matrix',      roles: ['SUPER_ADMIN'] },

  // ── HEALTHCARE module — daily operations ───────────────────────────────────
  // Only rendered when user.modules includes 'HEALTHCARE'.
  // permission: key ties visibility to role_default_permissions in DB —
  // Tenant Admin can grant/revoke per role without a code change.
  {
    labelKey: 'navigation.patients',     icon: MdPeople,        to: '/healthcare/patients',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'HEALTHCARE',  permission: 'healthcare:patients:read',
  },
  {
    labelKey: 'navigation.appointments', icon: MdCalendarMonth, to: '/healthcare/appointments',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'HEALTHCARE',  permission: 'healthcare:appointments:read',
  },
  {
    labelKey: 'navigation.hcInvoices',  icon: MdReceipt,       to: '/healthcare/invoices',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'HEALTHCARE',  permission: 'healthcare:invoices:read',
  },
  {
    // STAFF excluded — day-to-day clinic staff don't need analytics.
    // READ_ONLY excluded — reports contain aggregated financial data.
    labelKey: 'navigation.hcReports',   icon: MdAssessment,    to: '/healthcare/reports',
    roles:  ['ADMIN','MANAGER','DOCTOR'],
    module: 'HEALTHCARE',  permission: 'reports:healthcare:read',
  },

  // ── FACTORY module — daily operations ──────────────────────────────────────
  {
    labelKey: 'navigation.products',          icon: MdInventory2,    to: '/factory/products',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',          permission: 'factory:products:read',
  },
  {
    label: 'Variant Catalog', icon: MdCategory, to: '/factory/variant-catalog',
    roles:  ['ADMIN','MANAGER'],
    module: 'FACTORY', permission: 'factory:products:read',
    requiresFeature: 'FACTORY_PRODUCT_VARIANTS',
  },
  {
    label: 'Variant Orders', icon: MdShoppingCart, to: '/factory/variant-orders',
    roles:  ['ADMIN','MANAGER'],
    module: 'FACTORY', permission: 'factory:orders:write',
    requiresFeature: 'FACTORY_PRODUCT_VARIANTS',
  },
  {
    label: 'Decimal Inventory', icon: MdStraighten, to: '/factory/decimal-inventory',
    roles:  ['ADMIN','MANAGER'],
    module: 'FACTORY', permission: 'factory:products:read',
    requiresFeature: 'FACTORY_DECIMAL_INVENTORY',
  },
  {
    labelKey: 'navigation.productionOrders', icon: MdFactory,       to: '/factory/orders',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',          permission: 'factory:orders:read',
  },
  {
    labelKey: 'navigation.tradePartners', icon: MdPeople,        to: '/factory/customers',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',          permission: 'factory:customers:read',
    requiresFeature: 'FACTORY_PARTNERS',
  },
  {
    labelKey: 'navigation.deliveries',        icon: MdLocalShipping, to: '/factory/deliveries',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',          permission: 'factory:deliveries:read',
  },
  {
    label: 'Shop', icon: MdStorefront, to: '/factory/partner-catalog',
    roles:  ['ADMIN','MANAGER','PARTNER','MARKETING'],
    permission: 'factory:partner_orders:request',
    requiresFeature: 'FACTORY_PARTNER_ORDERING',
  },
  {
    label: 'My Cart', icon: MdShoppingCart, to: '/factory/partner-cart',
    roles:  ['ADMIN','MANAGER','PARTNER','MARKETING'],
    permission: 'factory:partner_orders:request',
    requiresFeature: 'FACTORY_PARTNER_ORDERING',
  },
  {
    label: 'My Orders', icon: MdReceipt, to: '/factory/partner-my-orders',
    roles:  ['ADMIN','MANAGER','PARTNER','MARKETING'],
    permission: 'factory:partner_orders:request',
    requiresFeature: 'FACTORY_PARTNER_ORDERING',
  },
  {
    labelKey: 'navigation.partnerOrderRequests', icon: MdShoppingCart, to: '/factory/partner-order-requests',
    roles:  ['ADMIN','MANAGER'],
    module: 'FACTORY', permission: 'factory:partner_orders:review',
    requiresFeature: 'FACTORY_PARTNER_ORDERING',
  },
  {
    labelKey: 'navigation.factoryReports',   icon: MdAssessment,    to: '/factory/reports',
    roles:  ['ADMIN','MANAGER'],
    module: 'FACTORY',          permission: 'reports:factory:read',
    requiresFeature: 'FACTORY_REPORTS',
  },
  {
    // Billing invoices live under the FACTORY domain.
    // READ_ONLY can view but not create.
    labelKey: 'navigation.invoices',          icon: MdReceipt,       to: '/billing/invoices',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',          permission: 'billing:invoices:read',
  },
  {
    label: 'Daily Expenses', icon: MdAttachMoney, to: '/billing/daily-expenses',
    roles: ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    permission: 'expense:read',
    requiresFeature: 'DAILY_EXPENSES',
  },

  {
    labelKey: 'navigation.profitLoss',     icon: MdTrendingUp,    to: '/reports/profit-loss',
    roles:  ['ADMIN'],
    module: 'FACTORY',          permission: 'reports:profitloss:read',
  },

  // ── PHARMACY module ────────────────────────────────────────────────────────
  {
    labelKey: 'navigation.medicines',    icon: MdLocalPharmacy, to: '/pharmacy/medicines',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'PHARMACY',    permission: 'pharmacy:medicines:read',
  },
  {
    // DOCTOR excluded — physical rack management is a stock-keeper task.
    labelKey: 'navigation.racks',        icon: MdWarehouse,     to: '/pharmacy/racks',
    roles:  ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'PHARMACY',    permission: 'pharmacy:medicines:read',
  },
  {
    labelKey: 'navigation.rxInvoices',  icon: MdReceipt,       to: '/pharmacy/invoices',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'PHARMACY',    permission: 'pharmacy:invoices:read',
  },
  {
    // Same permission key as HC Reports — analytics reports for clinical data.
    labelKey: 'navigation.rxReports',   icon: MdBarChart,      to: '/pharmacy/reports',
    roles:  ['ADMIN','MANAGER','DOCTOR'],
    module: 'PHARMACY',    permission: 'reports:healthcare:read',
  },
  {
    // Vendor management — MANAGER-and-above only.
    labelKey: 'navigation.suppliers',    icon: MdBusiness,      to: '/pharmacy/suppliers',
    roles:  ['ADMIN','MANAGER'],
    module: 'PHARMACY',    permission: 'pharmacy:suppliers:write',
  },
  {
    labelKey: 'navigation.purchaseGrn', icon: MdLocalShipping, to: '/pharmacy/purchase-orders',
    roles:  ['ADMIN','MANAGER'],
    module: 'PHARMACY',    permission: 'pharmacy:suppliers:write',
  },
  {
    // DOCTOR included — a doctor may need to look up a patient's purchase history.
    labelKey: 'navigation.customers',    icon: MdPeople,        to: '/pharmacy/customers',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'PHARMACY',    permission: 'pharmacy:medicines:read',
  },
  {
    labelKey: 'navigation.returns',      icon: MdUndo,          to: '/pharmacy/returns',
    roles:  ['ADMIN','MANAGER','STAFF'],
    module: 'PHARMACY',    permission: 'pharmacy:returns:write',
  },
  {
    labelKey: 'navigation.medOrders',  icon: MdShoppingCart,  to: '/pharmacy/medicine-orders',
    roles:  ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'],
    module: 'PHARMACY',    permission: 'pharmacy:medicines:read',
  },
  // ── WORKFORCE ─────────────────────────────────────────────────────────────
  // Available for ALL modules. Super Admin can disable per-tenant via the
  // WORKFORCE feature flag (removes from sidebar entirely when disabled).
  // Each item is still permission-gated so the right roles see the right tabs.
  {
    labelKey: 'navigation.workers',         icon: MdEngineering,            to: '/workforce/workers',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:workers:read',
    requiresFeature: 'WORKFORCE',
  },
  {
    labelKey: 'navigation.attendance',      icon: MdCoPresent,              to: '/workforce/attendance',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:attendance:read',
    requiresFeature: 'WORKFORCE',
  },
  {
    // Production tracking — factory-specific (brick/unit output per worker)
    labelKey: 'navigation.production',      icon: MdPrecisionManufacturing, to: '/workforce/production',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    module: 'FACTORY',
    permission: 'workforce:production:read',
    requiresFeature: 'LOG_PRODUCTION',
  },
  {
    label: 'Machine Production',            icon: MdPrecisionManufacturing, to: '/factory/machine-production',
    roles: ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    module: 'FACTORY',
    requiresFeature: 'MACHINE_PRODUCTION',
  },
  {
    labelKey: 'navigation.workerPayments', icon: MdAccountBalance,         to: '/workforce/payments',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:payments:read',
    requiresFeature: 'WORKFORCE',
  },
  {
    labelKey: 'navigation.workforceReports',      icon: MdTrendingUp,             to: '/workforce/reports',
    roles: ['ADMIN'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:reports:read',
    requiresFeature: 'WORKFORCE',
  },
  // ── INVENTORY ─────────────────────────────────────────────────────────────
  // Independent premium pack for vendor / asset management.
  {
    labelKey: 'navigation.vendors',         icon: MdStorefront,             to: '/workforce/vendors',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:vendors:read',
    requiresFeature: 'INVENTORY',
  },
  {
    labelKey: 'navigation.resourceInventory', icon: MdInventory2,             to: '/workforce/resources',
    roles: ['ADMIN','MANAGER','STAFF','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:resources:read',
    requiresFeature: 'INVENTORY',
  },
  {
    labelKey: 'navigation.equipmentTracker',       icon: MdBuild,                  to: '/workforce/equipment',
    roles: ['ADMIN','MANAGER','READ_ONLY'],
    requiresAnyModule: ['FACTORY','HEALTHCARE'],
    permission: 'workforce:equipment:read',
    requiresFeature: 'EQUIPMENT_TRACKER',
  },
  {
    // Sites — only visible when Super Admin enables the MULTI_SITE premium feature
    labelKey: 'navigation.siteManagement',           icon: MdLocationOn,             to: '/workforce/sites',
    roles: ['ADMIN'],
    requiresAnyModule: ['FACTORY','HEALTHCARE','PHARMACY'],
    permission: 'workforce:sites:write',
    requiresFeature: 'MULTI_SITE',
  },

  // ── ADMIN / SETTINGS (bottom) ─────────────────────────────────────────────
  // Configuration and setup items — rarely used day-to-day.
  // Grouped at the bottom per SaaS convention (Zoho, QuickBooks, ERPNext).
  // Per-module guards ensure each item is only visible to the relevant tenant.

  // Healthcare admin settings
  {
    // ADMIN-only: doctor management is a configuration task, not a daily workflow.
    labelKey: 'navigation.doctorList',          icon: MdLocalHospital,  to: '/healthcare/doctors',
    roles:  ['ADMIN'],
    module: 'HEALTHCARE',      permission: 'healthcare:doctors:write',
  },

  // Factory admin settings
  {
    labelKey: 'navigation.settings', icon: MdSettings,       to: '/settings/payment',
    roles:  ['ADMIN'],
    module: 'FACTORY',
  },

  // Pharmacy admin settings
  {
    labelKey: 'navigation.pharmacySettings', icon: MdSettings,       to: '/pharmacy/settings',
    roles:  ['ADMIN'],
    module: 'PHARMACY',
  },

  // Tenant-wide admin settings (visible regardless of active module)
  { labelKey: 'navigation.allUsers',             icon: MdManageAccounts, to: '/users', roles: ['ADMIN'] },
  { labelKey: 'settings.languagePreferences',    icon: MdAdminPanelSettings, to: '/settings/language-preferences', roles: ['ADMIN'], requiresI18n: true },
  { labelKey: 'navigation.tenantChartOfAccounts', icon: MdAccountBalance, to: '/settings/chart-of-accounts',  roles: ['ADMIN'], requiresFeature: 'BUSINESS_SETUP' },
  { labelKey: 'navigation.tenantMasterTypes',      icon: MdSettings,       to: '/settings/master-types',       roles: ['ADMIN'], requiresFeature: 'BUSINESS_SETUP' },
  { label: 'Terminology',          icon: MdEdit,          to: '/settings/terminology',        roles: ['ADMIN'], requiresFeature: 'TERMINOLOGY_CONTROLS' },
  { labelKey: 'navigation.myReferral',      icon: MdShare,          to: '/settings/referral',            roles: ['ADMIN','MANAGER','STAFF','DOCTOR','READ_ONLY'], requiresFeature: 'REFERRAL' },
  { label: 'Barcode / QR Generator',        icon: MdQrCode2,        to: '/settings/barcode-qr',         roles: ['ADMIN'], requiresAnyFeature: ['BARCODE_GENERATOR', 'QR_GENERATOR', 'BARCODE_QR_GENERATOR'] },
  { labelKey: 'navigation.dataCleanup',      icon: MdSecurity,       to: '/settings/cleanup-data',        roles: ['ADMIN'] },
  { labelKey: 'navigation.mfaSecurity',        icon: MdSecurity,      to: '/mfa-setup',                     roles: ['ADMIN','DOCTOR','STAFF','MANAGER','READ_ONLY'], permission: 'user:mfa:manage' },

  // Super Admin reference data
  { labelKey: 'navigation.masterTypes',      icon: MdSettings,       to: '/super-admin/master-types',    roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.financialAccounts', icon: MdAccountBalance, to: '/super-admin/financial-accounts', roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.referralManagement',         icon: MdShare,          to: '/super-admin/referrals',        roles: ['SUPER_ADMIN'] },
  { labelKey: 'navigation.testimonials',      icon: MdStar,           to: '/super-admin/testimonials',     roles: ['SUPER_ADMIN'] },

  // Audit Log — very bottom; admin/super-admin only, infrequently accessed
  { labelKey: 'navigation.auditLog',         icon: MdManageHistory,  to: '/super-admin/audit-log',       roles: ['SUPER_ADMIN','ADMIN'] },
];

const ROLE_COLOR = {
  SUPER_ADMIN:     'bg-red-100 text-red-700',
  ADMIN:           'bg-purple-100 text-purple-700',
  DOCTOR:          'bg-blue-100 text-blue-700',
  STAFF:           'bg-green-100 text-green-700',
  MANAGER: 'bg-orange-100 text-orange-700',
  READ_ONLY:       'bg-neutral-200 text-neutral-600',
};

const NAV_LABEL_KEYS = {
  '/factory/products': 'factory.products',
  '/factory/variant-catalog': 'factory.variant-catalog',
  '/factory/variant-orders': 'factory.variant-orders',
  '/factory/orders': 'factory.orders',
  '/factory/customers': 'factory.customers',
  '/factory/deliveries': 'factory.deliveries',
  '/factory/partner-catalog': 'factory.partner-catalog',
  '/factory/partner-order-requests': 'factory.partner-order-requests',
  '/factory/reports': 'factory.reports',
  '/billing/invoices': 'billing.invoices',
  '/workforce/vendors': 'workforce.vendors',
  '/workforce/resources': 'workforce.resources',
  '/settings/referral': 'settings.referral',
};

const routeToNavKey = (to) => String(to || '').replace(/^\//, '').replace(/\//g, '.');
const SALES_VIEW_ROUTES = new Set([
  '/factory/products',
  '/factory/variant-catalog',
  '/factory/variant-orders',
  '/factory/orders',
  '/factory/customers',
  '/factory/deliveries',
  '/factory/partner-catalog',
  '/factory/partner-cart',
  '/factory/partner-my-orders',
  '/factory/partner-order-requests',
  '/factory/reports',
]);
const stripFactoryWord = (value) => String(value || '')
  .replace(/\bfactory\b/ig, ' ')
  .replace(/\s{2,}/g, ' ')
  .trim();

export default function Sidebar() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const open           = useSelector(selectSidebar);
  const user           = useSelector(selectUser);
  const role           = useSelector(selectRole);
  const modules        = useSelector(selectModules);
  const permissions    = useSelector(selectPermissions);
  const enabledFeatures      = useSelector(selectEnabledFeatures);
  const navigationLabels     = useSelector(selectNavigationLabels);
  const i18nEnabled          = useSelector(selectI18nEnabled);
  const supportedLanguages   = useSelector(selectSupportedLanguages);
  const currentLanguage      = useSelector(selectCurrentLanguage);
  const workforceLabel       = useSelector(selectFeatureLabel('WORKFORCE'));
  const inventoryLabel       = useSelector(selectFeatureLabel('INVENTORY'));
  const resourceSellMappingEnabled = useSelector(selectIsFeatureEnabled('RESOURCE_SELL_MAPPING'));
  const barcodeGeneratorEnabled = useSelector(selectIsFeatureEnabled('BARCODE_GENERATOR'));
  const qrGeneratorEnabled = useSelector(selectIsFeatureEnabled('QR_GENERATOR'));
  const legacyBarcodeQrGeneratorEnabled = useSelector(selectIsFeatureEnabled('BARCODE_QR_GENERATOR'));
  const workforceSectionLabel = workforceLabel === 'WORKFORCE' ? 'Workforce / HR Pack' : workforceLabel;
  const inventorySectionLabel = inventoryLabel === 'INVENTORY' ? 'Asset Pack' : inventoryLabel;
  const auditLogEnabled = useSelector(selectAuditLogEnabled);
  const adminTenantCleanupEnabled = useSelector(selectAdminTenantCleanupEnabled);
  const subscriptionActive = useSelector(selectSubscriptionActive);
  const tenantName     = useSelector(selectTenantName);
  const tenantLogoUrl  = useSelector(selectTenantLogoUrl);
  const barcodeQrNavLabel = legacyBarcodeQrGeneratorEnabled || (barcodeGeneratorEnabled && qrGeneratorEnabled)
    ? 'Barcode / QR Generator'
    : barcodeGeneratorEnabled
      ? 'Barcode Generator'
      : 'QR Generator';

  // Close sidebar on mobile when window resizes below 768px
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        dispatch(setSidebarOpen(false));
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  const handleLogout = async () => {
    try {
      // Must call server BEFORE clearing local state so the Bearer token is
      // still available. Server revokes the DB refresh token + expires the
      // httpOnly cookie (Max-Age=0). Without this, refreshing the page after
      // logout would re-authenticate the user silently.
      await authApi.logout();
    } catch {
      // Best effort — always clear local state even if server is unreachable.
    }
    dispatch(logout());
    navigate('/login');
  };

  const visible = NAV_ITEMS.filter(item => {
    if (item.roles && !item.roles.includes(role)) return false;
    if (role !== 'SUPER_ADMIN' && !subscriptionActive && item.to !== '/dashboard') return false;
    // SUPER_ADMIN bypasses module checks (system tenant has no modules)
    if (role === 'SUPER_ADMIN') return true;
    // Hide Audit Log from ADMIN if super-admin disabled it for their tenant
    if (item.to === '/super-admin/audit-log' && role === 'ADMIN' && !auditLogEnabled) return false;
    if (item.to === '/settings/cleanup-data' && role === 'ADMIN' && !adminTenantCleanupEnabled) return false;
    if (item.module && !modules.includes(item.module)) return false;
    // requiresAnyModule: item only shows if user has at least one of these modules
    if (item.requiresAnyModule && !item.requiresAnyModule.some(m => modules.includes(m))) return false;
    // requiresFeature: item only shows if the feature is enabled (tenant-wide or per-module)
    if (item.requiresFeature && !enabledFeatures.some(f => f === item.requiresFeature || f.startsWith(item.requiresFeature + ':'))) return false;
    // requiresAnyFeature: item shows when at least one of the listed features is enabled
    if (item.requiresAnyFeature && !item.requiresAnyFeature.some(feature => enabledFeatures.some(f => f === feature || f.startsWith(feature + ':')))) return false;
    // requiresFeatureAbsent: item is hidden if ANY of these features are enabled
    if (item.requiresFeatureAbsent && item.requiresFeatureAbsent.some(f => enabledFeatures.includes(f))) return false;
    // requiresI18n: item only appears when tenant i18n is enabled by SUPER_ADMIN
    if (item.requiresI18n && !i18nEnabled) return false;
    // Permission-gated items: if a permission key is specified, user must have it
    if (item.permission && permissions.length > 0 && !permissions.includes(item.permission)) return false;
    return true;
  });

  return (
    <aside className={`flex flex-col h-full bg-neutral-900 text-white transition-all duration-200
                       ${open ? 'w-60' : 'w-16'} flex-shrink-0
                       md:relative md:translate-x-0
                       ${open ? 'fixed inset-y-0 left-0 z-30 md:static' : 'fixed inset-y-0 -left-16 md:static md:left-0'}`}>
      {/* Logo + Toggle */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-neutral-700">
        {open && (
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={role === 'SUPER_ADMIN' || !tenantLogoUrl ? '/logo.png' : tenantLogoUrl}
              alt={tenantName || 'UnifiedOps'}
              className="h-7 w-auto max-w-[32px] object-contain shrink-0 rounded"
              onError={e => { e.target.onerror = null; e.target.src = '/logo.png'; }}
            />
            <span
              className="font-bold text-sm text-white truncate"
              title={tenantName || 'UnifiedOps'}
            >
              {tenantName || 'UnifiedOps'}
            </span>
          </div>
        )}
        {!open && (
          <img
            src={role === 'SUPER_ADMIN' || !tenantLogoUrl ? '/logo.png' : tenantLogoUrl}
            alt={tenantName || 'UnifiedOps'}
            className="h-7 w-auto max-w-[32px] object-contain mx-auto rounded"
            onError={e => { e.target.onerror = null; e.target.src = '/logo.png'; }}
          />
        )}
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="p-1.5 rounded-lg hover:bg-neutral-700 transition ml-auto shrink-0"
          aria-label="Toggle sidebar"
        >
          {open ? <MdChevronLeft size={20} /> : <MdChevronRight size={20} />}
        </button>
      </div>

      {/* User info */}
      {open && user && (
        <div className="px-4 py-3 border-b border-neutral-700">
          <p className="text-sm font-semibold truncate">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs text-neutral-400 truncate">{user.email}</p>
          <span className={`mt-1 inline-block text-xs px-2 py-0.5 rounded-full font-medium
                           ${ROLE_COLOR[role] || 'bg-neutral-700 text-neutral-300'}`}>
            {role?.replace('_', ' ')}
          </span>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
        {visible.map(({ labelKey, label, icon: Icon, to, requiresFeature }, idx) => {
          const sectionLabel = requiresFeature === 'WORKFORCE'
            ? workforceSectionLabel
            : requiresFeature === 'INVENTORY'
              ? inventorySectionLabel
              : null;
          const customNavLabel = navigationLabels[NAV_LABEL_KEYS[to] || routeToNavKey(to)];
          const rawNavLabel = customNavLabel
            ? customNavLabel
            : to === '/workforce/resources'
            ? (resourceSellMappingEnabled ? 'Inventory' : 'Resource')
            : to === '/settings/barcode-qr'
              ? barcodeQrNavLabel
            : label
              ? label
            : to === '/factory/reports'
              ? (resourceSellMappingEnabled ? 'Sales Reports' : t(labelKey, 'menu'))
              : t(labelKey, 'menu');
          const navLabel = resourceSellMappingEnabled && SALES_VIEW_ROUTES.has(to)
            ? (stripFactoryWord(rawNavLabel) || rawNavLabel)
            : rawNavLabel;
          const isFirstFeatureSection = sectionLabel
            && (idx === 0 || visible[idx - 1]?.requiresFeature !== requiresFeature);
          return (
            <React.Fragment key={to}>
              {isFirstFeatureSection && open && (
                <div className="px-3 pt-3 pb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-neutral-500">
                    {sectionLabel}
                  </span>
                </div>
              )}
              {isFirstFeatureSection && !open && (
                <div className="border-t border-neutral-700 mx-2 my-1" />
              )}
              <NavLink
                to={to}
                onClick={() => { if (window.innerWidth < 768) dispatch(setSidebarOpen(false)); }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                   transition-colors duration-150 group
                   ${isActive
                     ? 'bg-primary text-white'
                     : 'text-neutral-300 hover:bg-neutral-700 hover:text-white'}`
                }
              >
                <Icon size={20} className="flex-shrink-0" />
                {open && <span className="truncate">{navLabel}</span>}
              </NavLink>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Language switcher — only shown when i18n is enabled and 2+ languages available */}
      {i18nEnabled && supportedLanguages.length > 1 && (
        <div className="border-t border-neutral-700 px-2 py-2">
          {open ? (
            <div className="flex items-center gap-1 flex-wrap px-1">
              <MdLanguage size={16} className="text-neutral-400 shrink-0" />
              {supportedLanguages.map(code => {
                const lang = SUPPORTED_LANGUAGES[code];
                const isActive = currentLanguage === code;
                return (
                  <button
                    key={code}
                    onClick={() => dispatch(setCurrentLanguage(code))}
                    className={`text-xs px-2 py-1 rounded transition-colors font-medium ${
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-neutral-400 hover:bg-neutral-700 hover:text-white'
                    }`}
                    title={lang?.name}
                  >
                    {lang?.flag} {lang?.nativeName}
                  </button>
                );
              })}
            </div>
          ) : (
            <button
              onClick={() => {
                const next = supportedLanguages[(supportedLanguages.indexOf(currentLanguage) + 1) % supportedLanguages.length];
                dispatch(setCurrentLanguage(next));
              }}
              className="flex justify-center w-full p-2 rounded-lg hover:bg-neutral-700 text-neutral-400 hover:text-white transition-colors"
              title={`Switch language (current: ${SUPPORTED_LANGUAGES[currentLanguage]?.nativeName})`}
            >
              <MdLanguage size={20} />
            </button>
          )}
        </div>
      )}

      {/* Logout */}
      <div className="border-t border-neutral-700 px-2 py-3">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm
                     text-neutral-300 hover:bg-red-600 hover:text-white transition-colors"
        >
          <MdLogout size={20} className="flex-shrink-0" />
          {open && <span>{t('common.logout', 'button')}</span>}
        </button>
      </div>
    </aside>
  );
}
